import { Ref, ref } from 'vue'
import { useLogsStore } from '@/store/logs'
import router from '@/router'
import { Camera } from '@/types/Cameras'
export abstract class SourceHolder<T extends object> {
  abstract readonly supportedHeights: number[]
  cameraId: string
  protected readonly timestamp: number
  protected sourceData?: T
  protected camera: Camera
  private currentlyLoadingHeight?: number
  private currentMaxHeight: number = 0
  private loadingPromise?: Promise<string | null>
  private internalAbortController?: AbortController
  currentSource: Ref<string | null> = ref(null)
  isLoading: Ref<boolean> = ref(false)
  constructor(cameraId: string, timestamp: number, camera: Camera) {
    this.cameraId = cameraId
    this.timestamp = timestamp
    this.camera = camera
  }
  abstract ondblclick(): void
  abstract onContextMenu(): void

  abstract loadSourceData(signal: AbortSignal): Promise<T | null>

  abstract loadSource(height: number, signal: AbortSignal, isInView: boolean): Promise<string | null>

  ensureSourceIsLoaded(height: number, signal: AbortSignal, isInView: boolean): void {
    if (signal.aborted) return
    const quantizedHeight = this.quantizedHeight(height)

    if (quantizedHeight <= this.currentMaxHeight || quantizedHeight === this.currentlyLoadingHeight) {
      return
    }

    if (this.loadingPromise) {
      if (quantizedHeight > this.currentlyLoadingHeight!) {
        this.internalAbortController?.abort()
        this.loadingPromise.finally(() => {
          this.loadingPromise = undefined
          this.ensureSourceIsLoaded(height, signal, isInView)
        })
      }
      return
    }

    this.currentlyLoadingHeight = quantizedHeight
    const controller = new AbortController()
    this.internalAbortController = controller

    const onExternalAbort = () => controller.abort()
    signal.addEventListener('abort', onExternalAbort, { once: true })

    this.isLoading.value = true

    this.loadingPromise = (this.sourceData ? Promise.resolve(this.sourceData) : this.loadSourceData(controller.signal))
      .then(sourceData => {
        if (!sourceData) return null

        this.sourceData = sourceData
        return this.loadSource(quantizedHeight, controller.signal, isInView)
      })
      .then(
        result => {
          if (result) {
            this.currentMaxHeight = quantizedHeight
            this.currentSource.value = result
          }
          return result
        },
        error => {
          if (error.name !== 'AbortError') {
            useLogsStore().addLogEntry({
              level: 'ERROR',
              message: `Error loading source for camera ${this.cameraId}: ${error}`,
              tag: 'canvas',
              error: error instanceof Error ? error.stack : String(error),
            })
          }
          return null
        }
      )
      .finally(() => {
        this.isLoading.value = false
        this.loadingPromise = undefined
        this.currentlyLoadingHeight = undefined
        signal.removeEventListener('abort', onExternalAbort)
      })
  }
  abstract canShowTooltip(): boolean

  abstract getTooltipText(): string

  private quantizedHeight(height: number): number {
    const supportedHeights = this.supportedHeights
    if (supportedHeights.length === 0) return height
    let closestHeight = supportedHeights[0]
    for (const supportedHeight of supportedHeights) {
      if (Math.abs(supportedHeight - height) < Math.abs(closestHeight - height)) {
        closestHeight = supportedHeight
      }
    }
    return closestHeight
  }
}
const keepPerCamera = 20
export abstract class SourceProviderFactory<SH extends SourceHolder<object>> {
  private globalUniqueIdentifier?: string
  abstract readonly providerType: string
  protected sourceHolders: Map<string, Map<number, SH>> = new Map()

  private reinitializeSourceHolders() {
    const newGlobalUniqueIdentifier = router.currentRoute.value.params.globalUniqueIdentifier as string | undefined
    if (this.globalUniqueIdentifier !== newGlobalUniqueIdentifier) {
      this.globalUniqueIdentifier = newGlobalUniqueIdentifier
      this.sourceHolders = new Map()
    }
  }

  abstract createSourceHolder(cameraId: string, timestamp: number, camera: Camera): SH

  private purgeSourceHolders(cameraId: string, timestamp: number) {
    if (!this.sourceHolders.has(cameraId)) return
    const timestampMap = this.sourceHolders.get(cameraId)!
    const timestamps = Array.from(timestampMap.keys())
    if (timestamps.length <= keepPerCamera) return
    timestamps.sort((a, b) => Math.abs(a - timestamp) - Math.abs(b - timestamp))
    const timestampsToDelete = timestamps.slice(keepPerCamera)
    for (const ts of timestampsToDelete) {
      timestampMap.delete(ts)
    }
  }

  getOrCreateSourceHolder(cameraId: string, timestamp: number, camera: Camera): SH {
    this.reinitializeSourceHolders()
    if (!this.sourceHolders.has(cameraId)) {
      this.sourceHolders.set(cameraId, new Map())
    }
    if (!this.sourceHolders.get(cameraId)!.has(timestamp)) {
      const newSourceHolder = this.createSourceHolder(cameraId, timestamp, camera)
      this.sourceHolders.get(cameraId)!.set(timestamp, newSourceHolder)
    }
    this.purgeSourceHolders(cameraId, timestamp)
    return this.sourceHolders.get(cameraId)!.get(timestamp)!
  }
}
